import React, { useState } from "react";
import Icon from "./icons";
import Avatar from "./avatar";
import { groupChats, matchChats } from "../../../lib/artemis/present";
import { chatRows, isShared, scratchLabel, sessionGroups } from "../../../lib/artemis/hub";

// The ARTEMIS tab's hub (WEB-7168, Option B): the rail, the Chats and Sessions list column, and the
// Chats section's right column. The canvas "Artemis Redesign Options", boards "B · Web hub" and
// "Shared components".

const RAIL = [
  { section: 'chats', label: 'Chats', icon: 'sparkle' },
  { section: 'sessions', label: 'Sessions', icon: 'pin' },
  { section: 'alerts', label: 'Alerts', icon: 'bell' },
  { section: 'skills', label: 'Skills', icon: 'skill' }
]

const initialsOf = user => {
  if (!user) return '?'
  const name = [user.firstName, user.lastName].filter(Boolean).join(' ') || user.name || user.email || ''
  return name.split(/[\s@.]+/).filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join('') || '?'
}

// sections: the ones shown (Skills only where user skills are). badges: { alerts, mentions }.
// usage: usageState(...) or null; the usage button opens the usage menu (onUsage).
export const HubRail = ({ section, sections, onSection, badges, usage, usageOpen, onUsage, user }) => (
  <nav className="artemis-hub-rail" aria-label="Artemis sections">
    {RAIL.filter(r => sections.includes(r.section)).map(r => {
      const on = r.section === section
      const count = r.section === 'alerts' ? badges.alerts : r.section === 'sessions' ? badges.mentions : 0
      return (
        <button key={r.section} type="button" className={`artemis-hub-rail-item artemis-hub-rail-${r.section} ${on ? 'artemis-hub-rail-item-on' : ''}`}
          aria-current={on ? 'page' : undefined} onClick={() => onSection(r.section)}>
          <Icon name={r.icon} size={22} />
          <span className="artemis-hub-rail-label">{r.label}</span>
          {count > 0 && (
            <span className={`artemis-hub-badge ${r.section === 'alerts' ? 'artemis-hub-badge-alerts' : 'artemis-hub-badge-mention'}`}
              aria-label={r.section === 'alerts' ? `${count} new` : `${count} mentioning you`}>
              {r.section === 'sessions' ? `@${count}` : count}
            </span>
          )}
        </button>
      )
    })}
    <span className="artemis-hub-rail-spacer" />
    {usage && usage.metered && (
      <button type="button" className={`artemis-hub-usage artemis-usage-button ${usage.limit ? 'artemis-hub-usage-limit' : usage.warn ? 'artemis-hub-usage-warn' : ''}`}
        aria-label={`Artemis usage: ${usage.limit ? 100 : usage.percent}% used`} aria-haspopup="dialog" aria-expanded={usageOpen} onClick={onUsage}>
        <Icon name="chart" size={18} />
        <span>{usage.limit ? '100%' : `${usage.percent}%`}</span>
      </button>
    )}
    {user && <Avatar url={user.imageProfileUrl} initials={initialsOf(user)} name={[user.firstName, user.lastName].filter(Boolean).join(' ')} className="artemis-hub-me" />}
  </nav>
)

// A conversation's glyph: an ask, a session just for this user, or a shared session.
const glyph = (row, session) => (isShared(row) ? 'people' : session ? 'pin' : 'sparkle')

const HubRow = ({ row, session, active, onOpen, onDelete, onUnarchive }) => {
  const [confirming, setConfirming] = useState(false)
  // A shared session someone else owns is left, not deleted (DELETE does that for a member).
  const leaves = isShared(row) && row.owner === false
  const sub = (session && scratchLabel(row)) || row.preview
  if (confirming) {
    return (
      <div className="artemis-hub-row artemis-hub-row-confirm" role="group" aria-label={`Delete ${row.title}`}>
        <span className="artemis-flex">{leaves ? 'Leave this session?' : isShared(row) ? 'Delete for everyone?' : session ? 'Delete this session?' : 'Delete this chat?'}</span>
        <button type="button" className="artemis-small-button" onClick={() => setConfirming(false)}>Cancel</button>
        <button type="button" className="artemis-small-button artemis-small-button-danger" onClick={() => onDelete(row.id)}>{leaves ? 'Leave' : 'Delete'}</button>
      </div>
    )
  }
  // Unread counts only mean something in a shared session: in an ask every Artemis reply counts as unread
  // to the server, though the user watched it arrive.
  const unread = !active && isShared(row) && row.unread > 0 ? row.unread : 0
  const mentions = !active && row.unreadMentions > 0 ? row.unreadMentions : 0
  return (
    <div className={`artemis-hub-row-wrap ${active ? 'artemis-hub-row-on' : ''}`}>
      <button type="button" className="artemis-hub-row" aria-current={active || undefined} onClick={() => onOpen(row.id)}>
        <span className={`artemis-hub-glyph ${session ? 'artemis-hub-glyph-session' : ''}`} aria-hidden="true"><Icon name={glyph(row, session)} size={16} /></span>
        <span className="artemis-hub-row-text">
          <span className="artemis-hub-row-title">{row.title}</span>
          {sub && <span className="artemis-hub-row-sub">{sub}</span>}
        </span>
        <span className="artemis-hub-row-end">
          {/* An archived row's Unarchive takes the room its time would. */}
          {!onUnarchive && <span className="artemis-hub-row-time">{row.time}</span>}
          {(unread > 0 || mentions > 0) && (
            <span className="artemis-hub-row-badges">
              {unread > 0 && <span className="artemis-hub-badge artemis-hub-badge-unread" aria-label={`${unread} unread`}>{unread}</span>}
              {mentions > 0 && <span className="artemis-hub-badge artemis-hub-badge-mention" aria-label={`${mentions} mentioning you`}>@{mentions}</span>}
            </span>
          )}
        </span>
      </button>
      {onUnarchive
        ? <button type="button" className="artemis-small-button artemis-hub-unarchive" onClick={() => onUnarchive(row.id)}>Unarchive</button>
        : onDelete && (
          <button type="button" className="artemis-icon-button artemis-icon-button-sm artemis-hub-row-delete" title={leaves ? 'Leave session' : 'Delete'}
            aria-label={`${leaves ? 'Leave' : 'Delete'} ${row.title}`} onClick={() => setConfirming(true)}>
            <Icon name={leaves ? 'leave' : 'trash'} size={15} />
          </button>
        )}
    </div>
  )
}

// The Chats or Sessions list column. rows: every conversation row loaded (the alerts conversation
// included); opened: the conversations opened as a session in this browser. archived: the Archived
// chip's state ({ on, rows, state, onToggle, onUnarchive }).
export const ConversationList = ({ section, rows, opened, activeId, state, hasMore, onMore, onOpen, onNew, onDelete, archived }) => {
  const [query, setQuery] = useState('')
  const sessions = section === 'sessions'
  const source = archived.on ? archived.rows : rows
  const groups = sessions
    ? sessionGroups(matchChats(source, query), opened)
    : groupChats(matchChats(chatRows(source, opened), query))
  const loading = (archived.on ? archived.state : state) === 'loading'
  const failed = (archived.on ? archived.state : state) === 'failed'
  return (
    <aside className="artemis-panel artemis-hub-list" aria-label={sessions ? 'Sessions' : 'Chats'}>
      <div className="artemis-hub-list-head">
        <span className="artemis-hub-list-titles">
          <span className="artemis-hub-list-title">{sessions ? 'Sessions' : 'Chats'}</span>
          <span className="artemis-hub-list-sub">{sessions ? 'Scratchpad on · shared ones first' : 'Quick asks from the map and planner'}</span>
        </span>
        <button type="button" className="artemis-hub-new" title={sessions ? 'Start a session' : 'New chat'} aria-label={sessions ? 'Start a session' : 'New chat'} onClick={onNew}>
          <Icon name="plus" size={18} />
        </button>
      </div>
      <div className="artemis-hub-list-tools">
        <label className="artemis-history-search">
          <Icon name="find" size={16} />
          <input type="search" value={query} onChange={e => setQuery(e.target.value)} placeholder={sessions ? 'Search sessions' : 'Search chats'} aria-label={sessions ? 'Search sessions' : 'Search chats'} />
        </label>
        <button type="button" className={`artemis-hub-chip ${archived.on ? 'artemis-hub-chip-on' : ''}`} aria-pressed={archived.on} onClick={archived.onToggle}>Archived</button>
      </div>
      <div className="artemis-hub-list-body">
        {failed && <p className="artemis-muted artemis-hub-empty" role="status">Couldn’t load them. <button type="button" className="artemis-link-button" onClick={() => onMore(false)}>Try again</button></p>}
        {groups.length === 0 && !failed && (
          <p className="artemis-muted artemis-hub-empty">
            {loading ? 'Loading…'
              : query ? 'Nothing matches.'
              : archived.on ? (sessions ? 'No archived sessions.' : 'No archived chats.')
              : sessions ? 'A session keeps Artemis’s finds on a scratchpad until you send them, and lets partners join. Start one with +, or open a chat as a session.'
              : 'Your chats with Artemis show up here.'}
          </p>
        )}
        {groups.map(group => (
          <div key={group.label} className="artemis-hub-group">
            <span className="artemis-eyebrow artemis-hub-group-label">{group.label}</span>
            {group.chats.map(row => (
              <HubRow key={row.id} row={row} session={sessions} active={row.id === activeId} onOpen={onOpen}
                onDelete={archived.on ? null : onDelete} onUnarchive={archived.on ? archived.onUnarchive : null} />
            ))}
          </div>
        ))}
        {!archived.on && hasMore && (
          <button type="button" className="artemis-small-button artemis-load-more" disabled={loading} onClick={() => onMore(true)}>
            {loading ? 'Loading…' : 'Show more'}
          </button>
        )}
      </div>
    </aside>
  )
}

// The Chats section's right column: what Artemis reads of the map, and three starters.
export const SeesPanel = ({ view, starters, onAsk }) => (
  <aside className="artemis-panel artemis-hub-right artemis-hub-sees" aria-label="What Artemis sees">
    <span className="artemis-hub-right-title">What Artemis sees</span>
    {view.available
      ? (
        <div className="artemis-hub-sees-card">
          {view.rows.map(row => (
            <span key={row.k} className="artemis-hub-sees-row"><span className="artemis-hub-sees-k">{row.k}</span><span className="artemis-hub-sees-v">{row.v}</span></span>
          ))}
        </div>
      )
      : <p className="artemis-muted">Your map, once it has loaded.</p>}
    <p className="artemis-hub-note">Answers in a chat act on your map straight away. Open as session to collect finds on a scratchpad first, or to bring a partner in.</p>
    <span className="artemis-hub-right-title">Try</span>
    <div className="artemis-hub-starters">
      {starters.map(q => <button key={q} type="button" className="artemis-question" onClick={() => onAsk(q)}>{q}</button>)}
    </div>
  </aside>
)
